//============================================================================
// Name        : EditScheduleDialog.tsx
// Purpose     : Implement dialog for scheduling a production
// Created     : 2019-08-23
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line: no-unused-expression
/** @jsx jsx */ jsx;
import { css, jsx } from "@emotion/core";
import axios from "axios";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";
import Select from "react-select";
import * as GlobalSettings from "../../globalSettings";
import { ChangeInputEvent, EditScheduleActions, IProduction, IReduxReducers, IScheduleProductionDialog, IZone } from "./types";

declare const saveProductionsUrl: string;
declare const deleteProductionsUrl: string;

const titleStyle = css`
    font-size: 30px;
    font-family: "Open Sans", Arial, Helvetica, Sans-Serif;
`;
const productionSlotWrapper = css`
    display: flex;
    padding-bottom: 5px;
    flex-wrap: wrap;
    justify-content: space-evenly;
`;
const productionSlot = css`
    width: 175px;
`;
const productionDate = css`
    margin-top: 10px;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    background-color: #8ab4d5;
    color: white;
    text-align: center;
    font-size: 16px;
    padding-top: 2px;
    font-family: Verdana;
`;
const contentFields = css`
    & input {
        text-align: center;
    }
`;
const amountField = css`
    height: 28px;
    margin-bottom: 6px;
`;
const totalsStyle = css`
    font-size: 30px;
    font-family: "Open Sans", Arial, Helvetica, Sans-Serif;
    font-weight: 300;
    & span {
        font-weight: normal;
    }
`;

interface IScheduleProductionDialogState {
    productions: IProduction[];
    pastDueProductions: IProduction[];
    allProductionsHaveZone: boolean;
}

class EditScheduleDialog extends React.Component<IScheduleProductionDialog, IScheduleProductionDialogState> {
    constructor(props: IScheduleProductionDialog) {
        super(props);

        // Set default state
        this.state = {
            productions: [] as IProduction[],
            pastDueProductions: [] as IProduction[],
            allProductionsHaveZone: true
        };
    }

    componentWillReceiveProps(props: IScheduleProductionDialog) {
        const productions = this.props.productions.filter(p => p.scheduler_user_id == props.scheduleDialogUserId);

        let startDate = moment().add(this.props.startDay, "days");
        const days = (this.props.endDay + 1) - this.props.startDay;
        const it = Array(days).fill(1);
        for (const i of it) {
            // Add missing empty productions
            const dayProduction = productions.find(p => p.scheduled_date == startDate.format("YYYY-MM-DD"));
            if (!dayProduction) {
                // Add empty production
                const newProduction: IProduction = {
                    scheduler_name: this.getUserName(props.scheduleDialogUserId),
                    scheduler_user_id: props.scheduleDialogUserId,
                    scheduled_date: startDate.format("YYYY-MM-DD"),
                    production_item: this.props.product.alu,
                    product_name: this.props.product.description,
                    storage_unit_name: "LBS",
                    qty_produced: 0,
                    status: "Pending"
                } as IProduction;
                productions.push(newProduction);
            }
            startDate = startDate.add(i, "days");
        }

        productions.sort((a, b) => {
            const aDate = moment(a.scheduled_date, "YYYY-MM-DD");
            const bDate = moment(b.scheduled_date, "YYYY-MM-DD");
            return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
        });
        this.setState({
            productions,
            pastDueProductions: this.props.pastDueProductions.filter(p => p.scheduler_user_id == props.scheduleDialogUserId)
        });
    }

    getQtyForProduction = (date: string): string => {
        const allProductions = this.state.pastDueProductions.concat(this.state.productions);
        const production = allProductions.find(p => p.scheduled_date == date);
        if (production) {
            return String(production.needed_qty);
        }
        return "0";
    }

    formatDate = (date: string): string => moment(date, "YYYY-MM-DD").format("MM/DD/YYYY");

    getUserName = (userId: number): string => {
        if (userId == -1) {
            return "";
        }
        const user = this.props.schedulerUsers.find(u => u.id == userId);
        return `${user.first_name} ${user.last_name}`;
    }

    getClassName = () => this.props.show ? "modal show" : "modal fade";

    // Update a production quantity
    onAmountChange(date: string, event: ChangeInputEvent) {
        // Try to get the production on past-due
        const pastDueProductions = this.state.pastDueProductions.map(p => {
            if (p.scheduled_date == date) {
                p.needed_qty = parseInt(event.target.value, 10);
                p.pending_qty = p.needed_qty - p.qty_produced;
            }
            return p;
        });

        // Try to get the production on future productions
        const productions = this.state.productions.map(p => {
            if (p.scheduled_date == date) {
                p.needed_qty = parseInt(event.target.value, 10);
                p.pending_qty = p.needed_qty - p.qty_produced;
            }
            return p;
        });

        let allProductionsHaveZone = true;
        productions.map(p => {
            if (p.needed_qty > 0 && !p.sub_zone_id) {
                allProductionsHaveZone = false;
            }
        });

        this.setState({
            pastDueProductions,
            productions,
            allProductionsHaveZone
        });
    }

    onZoneChange(date: string, event: any) {
        // Try to get the production on past-due
        const pastDueProductions = this.state.pastDueProductions.map(p => {
            if (p.scheduled_date == date) {
                p.sub_zone_id = event.value;
            }
            return p;
        });

        // Try to get the production on future productions
        const productions = this.state.productions.map(p => {
            if (p.scheduled_date == date) {
                p.sub_zone_id = event.value;
            }
            return p;
        });

        let allProductionsHaveZone = true;
        productions.map(p => {
            if (p.needed_qty > 0 && !p.sub_zone_id) {
                allProductionsHaveZone = false;
            }
        });

        this.setState({
            pastDueProductions,
            productions,
            allProductionsHaveZone
        });
    }

    zoneToSelectValue = (date: string) => {
        const production = this.state.pastDueProductions.concat(this.state.productions).find(p => p.scheduled_date == date);
        if (production == null || production.sub_zone_id == null) {
            return null;
        }

        const zones = [];
        for (const zone in this.props.subzones) {
            if (this.props.subzones.hasOwnProperty(zone)) {
                const subzones = this.props.subzones[zone] as [];
                subzones.map((z: IZone) => {
                    zones.push( { id: z.id, name: `${zone} - ${z.name}` } );
                });
            }
        }

        const selectedZone = zones.find(z => z.id == production.sub_zone_id);
        return { value: production.sub_zone_id, label: selectedZone.name };
    }

    getUserPastDueTotal = () => {
        return this.state.pastDueProductions.reduce((accTotal, production) => accTotal + production.needed_qty, 0);
    }

    getTotalValue = () => {
        return this.state.productions.concat(this.state.pastDueProductions)
            .reduce((accTotal, production) => accTotal + (production.needed_qty || 0), 0);
    }

    shouldCancel = () => this.onClose();
    onClose = () => this.props.hideScheduleProduction();

    onSave = async () => {
        // Delete removed Productions
        let productions = this.state.pastDueProductions.concat(this.state.productions)
            .filter(p => (p.needed_qty || 0) == 0 && p.id);
        await axios.post(deleteProductionsUrl, { productions, authenticity_token: $('meta[name="csrf-token"]').attr("content") });

        // Save productions
        productions = this.state.pastDueProductions.concat(this.state.productions)
            .filter(p => (p.needed_qty || 0) > 0 && p.sub_zone_id);
        await axios.post(saveProductionsUrl, { productions, authenticity_token: $('meta[name="csrf-token"]').attr("content") });

        // Reload the page
        location.reload();
    }

    render() {
        const zones = [];
        for (const zone in this.props.subzones) {
            if (this.props.subzones.hasOwnProperty(zone)) {
                const subzones = this.props.subzones[zone] as [];
                subzones.map((z: IZone) => {
                    zones.push( { value: z.id, label: `${zone} - ${z.name}` } );
                });
            }
        }

        return (
            <React.Fragment>
                <div className={this.getClassName()} tabIndex={-1} role="dialog">
                    <div className="modal-dialog" css={{ width: "800px" }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <button onClick={this.shouldCancel} type="button" className="close" data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                <h1 className="modal-title">Schedule Production</h1>
                            </div>

                            <div className="modal-body">
                                <div css={[titleStyle, { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginLeft: 10, marginRight: 10 }]}>
                                    <div>{this.getUserName(this.props.scheduleDialogUserId)}</div>
                                    <div css={{ display: "flex", alignItems: "baseline", minWidth: 150, justifyContent: "flex-end" }}>
                                        <div css={{ fontSize: 32, color: "#f04d17", fontWeight: "bold", marginRight: 10 }}>{this.getUserPastDueTotal()}</div>
                                        <div css={{ fontSize: 19 }}>Past-Due</div>
                                    </div>
                                </div>

                                {/* Loop this block for all days in view */}
                                <div css={productionSlotWrapper}>
                                    {/* Past-Due Productions */}
                                    {this.state.pastDueProductions.map((p: IProduction, key) =>
                                        <div css={productionSlot} key={key}>
                                            <div css={productionDate}>{this.formatDate(p.scheduled_date)}</div>
                                            <div css={contentFields}>

                                                <input css={amountField} className="form-control" type="number"
                                                    onChange={this.onAmountChange.bind(this, p.scheduled_date)}
                                                    value={this.getQtyForProduction(p.scheduled_date)} />

                                                <Select placeholder="Select Location..." options={zones} styles={GlobalSettings.selectCustomStyle}
                                                    onChange={this.onZoneChange.bind(this, p.scheduled_date)}
                                                    value={this.zoneToSelectValue(p.scheduled_date)} />
                                            </div>
                                        </div>)}

                                    {/* Future Productions */}
                                    {this.state.productions.map((p: IProduction, key) =>
                                        <div css={productionSlot} key={key}>
                                            <div css={productionDate}>{this.formatDate(p.scheduled_date)}</div>
                                            <div css={contentFields}>

                                                <input css={amountField} className="form-control" type="number"
                                                    onChange={this.onAmountChange.bind(this, p.scheduled_date)}
                                                    value={this.getQtyForProduction(p.scheduled_date)} />

                                                <Select placeholder="Select Location..." options={zones} styles={GlobalSettings.selectCustomStyle}
                                                    onChange={this.onZoneChange.bind(this, p.scheduled_date)}
                                                    value={this.zoneToSelectValue(p.scheduled_date)} />
                                            </div>
                                        </div>)}
                                </div>
                            </div>

                            <div className="modal-footer" css={{ padding: "15px" }}>
                                <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div css={totalsStyle}>Total <span>{this.getTotalValue()}</span> lbs</div>
                                    <div className="actions-footer">
                                        <button className="man-btn man-green-btn" disabled={!this.state.allProductionsHaveZone} onClick={this.onSave}>Save</button>
                                        <button className="man-btn" onClick={this.shouldCancel}>Cancel</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: IReduxReducers) => ({
    show:                   state.editSchedule.showScheduleProdDialog,
    product:                state.editSchedule.product,
    startDay:               state.editSchedule.startDay,
    endDay:                 state.editSchedule.endDay,
    zones:                  state.editSchedule.zones,
    subzones:               state.editSchedule.subzones,
    schedulerUsers:         state.editSchedule.schedulerUsers,
    productions:            state.editSchedule.productions,
    pastDueProductions:     state.editSchedule.pastDueProductions,
    scheduleDialogUserId:   state.editSchedule.scheduleDialogUserId,
    scheduleDialogUsername: state.editSchedule.scheduleDialogUsername
});
const mapDispatchToProps = dispatch => ({
    hideScheduleProduction: () => dispatch({
        type: EditScheduleActions.HideScheduleProduction
    })
});

export default connect(mapStateToProps, mapDispatchToProps)(EditScheduleDialog);
